import { AlertTriangle, ArrowRight, CheckCircle2, Stethoscope } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import { Callout, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton, Switch } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { ApiError, apiUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTenants } from "@/lib/queries";
import {
  SMS_PROVIDERS, checkSummary, defaultPublicUrl, inboxText, maskedUrl, smsChanged, smsPayload, smsProblem,
  smsSections, smsValues,
  type SmsCheck, type SmsHook, type SmsNumber, type SmsSettings, type SmsValues,
} from "@/lib/sms-settings";
import { useSaveSmsSettings, useSetSmsNumber, useSmsCheck, useSmsSettings } from "@/lib/sms-settings-api";
import { cn, formatPhoneNumber, relativeTime } from "@/lib/utils";

/**
 * SMS settings (ADR-099 in platform): how the tenant's texts leave, on one screen.
 *
 * Top to bottom is the order it is set up in. The provider and its account. The two URLs Twilio
 * calls, which saving made: one is pasted into Twilio, the other is sent with every text. The
 * numbers, and which of them send. A check that reads the Twilio account and says what is still
 * wrong, so nobody finds out from a text that failed.
 *
 * The page shows only what the provider picked has a use for (`smsSections`): nothing but the
 * picker for Off, the numbers for the sandbox, and for Twilio the account, then, once it is saved,
 * the URLs, the numbers and the check.
 *
 * Admins only, as core-api's routes are. A superadmin with more than one tenant picks the tenant.
 */
export function SmsSettingsPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const tenants = useTenants({ enabled: isSuperadmin });
  const pickTenant = isSuperadmin && (tenants.data?.length ?? 0) > 1;
  const [picked, setPicked] = React.useState<number>();
  const tenantId = pickTenant ? (picked ?? tenants.data?.[0]?.id) : undefined;
  // A superadmin's own tenant is nobody's: wait to know which tenants there are.
  const settings = useSmsSettings(tenantId, !isSuperadmin || !tenants.isPending);

  return (
    <Page>
      <PageHeader
        title="SMS"
        description="How your texts leave: the provider, its account, the numbers that send, and the two URLs the provider calls."
        action={
          pickTenant && (
            <Select value={String(tenantId)} onValueChange={(value) => setPicked(Number(value))}>
              <SelectTrigger aria-label="Tenant" className="w-52">
                <SelectValue placeholder="Choose tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.data?.map((tenant) => (
                  <SelectItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />
      {settings.isPending ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : settings.error || !settings.data ? (
        <ErrorState
          className="min-h-[40vh]"
          error={settings.error}
          title="Could not load the SMS settings"
          action={
            <Button variant="outline" onClick={() => void settings.refetch()}>
              Try again
            </Button>
          }
        />
      ) : (
        // Keyed by the tenant and by when the settings were last saved: the form is loaded again for
        // another tenant and after a save, and not each time the settings are fetched. Switching a
        // number on refetches them, and must not wipe a token being typed.
        <SmsSettingsBody
          key={`${settings.data.tenant_id}:${settings.data.updated_at ?? "new"}`}
          settings={settings.data}
          tenantId={tenantId}
        />
      )}
    </Page>
  );
}

function SmsSettingsBody({ settings, tenantId }: { settings: SmsSettings; tenantId: number | undefined }) {
  const toast = useToast();
  const save = useSaveSmsSettings(tenantId);
  const check = useSmsCheck(tenantId);
  const fallback = defaultPublicUrl(apiUrl(""), window.location.origin);
  // What the form was loaded with, which a change is measured against.
  const [loaded] = React.useState<SmsValues>(() => smsValues(settings, fallback));
  const [values, setValues] = React.useState<SmsValues>(loaded);
  const [touched, setTouched] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const set = (patch: Partial<SmsValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setSaveError(null);
  };
  const problem = smsProblem(values, settings);
  const changed = smsChanged(values, loaded);
  const twilio = values.provider === "twilio";
  // Only what the provider picked has a use for (`smsSections`).
  const sections = smsSections(values.provider, settings);
  const tokenKept = settings.twilio_auth_token_set && settings.twilio_account_sid === values.accountSid.trim();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (problem) return;
    setSaveError(null);
    try {
      const saved = await save.mutateAsync(smsPayload(values, settings));
      toast.success(
        "SMS settings saved",
        saved.provider === "twilio"
          ? "Twilio accepted the keys."
          : saved.provider === "sandbox"
            ? "Every text is taken and none is sent."
            : "Nothing is sent.",
      );
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : "Could not save the settings. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <form onSubmit={(event) => void submit(event)}>
          <CardHeader title="Provider" description="Who carries your texts out, and with which account." />
          <CardBody className="space-y-4">
            <div role="radiogroup" aria-label="Provider" className="grid gap-2 sm:grid-cols-3">
              {SMS_PROVIDERS.map((option) => {
                const selected = values.provider === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => set({ provider: option.value })}
                    className={cn(
                      "rounded-md border px-3 py-2 text-left transition-colors",
                      selected
                        ? "border-accent-8 bg-accent-2 ring-2 ring-accent-8/25"
                        : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
                    )}
                  >
                    <span className="block text-sm font-medium text-text">{option.label}</span>
                    <span className="mt-0.5 block text-xs text-text-muted">{option.description}</span>
                  </button>
                );
              })}
            </div>

            {sections.account && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Account SID"
                  htmlFor="sms-account-sid"
                  required
                  hint={
                    settings.twilio_account_name && settings.twilio_account_sid === values.accountSid.trim()
                      ? `Twilio calls this account ${settings.twilio_account_name}.`
                      : "Starts with AC. Use the live one: the account's test credentials send nothing."
                  }
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="AC…"
                    value={values.accountSid}
                    onChange={(event) => set({ accountSid: event.target.value })}
                  />
                </Field>
                <Field
                  label={tokenKept ? "Replace the Auth Token" : "Auth Token"}
                  htmlFor="sms-auth-token"
                  required={!tokenKept}
                  hint={
                    tokenKept
                      ? "A token is kept. Leave blank to keep it."
                      : "Checked with Twilio when you save, and never shown again."
                  }
                >
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={values.authToken}
                    onChange={(event) => set({ authToken: event.target.value })}
                  />
                </Field>
                <Field
                  label="Messaging Service SID"
                  htmlFor="sms-service-sid"
                  hint="Optional. Starts with MG: Messaging, Services in the Twilio console. Every number that sends has to be in its Sender Pool."
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="MG…"
                    value={values.serviceSid}
                    onChange={(event) => set({ serviceSid: event.target.value })}
                  />
                </Field>
                <Field
                  label="Daily limit"
                  htmlFor="sms-daily-limit"
                  hint="Parts a day, counted from midnight Pacific. A long text is several parts. 0 is no limit."
                >
                  <Input
                    inputMode="numeric"
                    value={values.dailyLimit}
                    onChange={(event) => set({ dailyLimit: event.target.value })}
                  />
                </Field>
                <Field
                  className="sm:col-span-2"
                  label="Public address"
                  htmlFor="sms-public-url"
                  required
                  hint="Where Twilio reaches this system, up to /hooks. The two URLs Twilio calls are built from it."
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="https://console.example.com/api"
                    value={values.publicUrl}
                    onChange={(event) => set({ publicUrl: event.target.value })}
                  />
                </Field>
              </div>
            )}

            {sections.note && <p className="text-xs text-text-muted">{sections.note}</p>}
            {touched && problem && <Callout tone="danger">{problem}</Callout>}
            {saveError && <Callout tone="danger">{saveError}</Callout>}
          </CardBody>
          <CardFooter className="justify-between">
            <span className="text-xs text-text-muted">
              {twilio ? "Twilio is asked about the keys when you save. Nothing is saved if it says no." : ""}
            </span>
            <Button type="submit" variant="default" loading={save.isPending} disabled={!changed}>
              Save
            </Button>
          </CardFooter>
        </form>
      </Card>

      {sections.urls && (
        <Card className="overflow-hidden">
          <CardHeader
            title="The URLs Twilio calls"
            description="Saving made two triggers. They are on the Triggers page too. Each URL has its trigger's secret in it."
          />
          <CardBody className="space-y-5">
            {settings.incoming && (
              <HookRow
                title="Texts that arrive"
                hook={settings.incoming}
                lastWord="Last text"
                hasAddress={Boolean(settings.public_url)}
              >
                Paste this in Twilio. One number: Phone Numbers, the number, Messaging configuration, "A message
                comes in", Webhook, HTTP POST. Every number of a Messaging Service: Messaging, Services, the
                service, Integration, "Send a webhook".
              </HookRow>
            )}
            {settings.reports && (
              <HookRow
                title="Delivery reports"
                hook={settings.reports}
                lastWord="Last report"
                hasAddress={Boolean(settings.public_url)}
              >
                Nothing to paste. This URL is sent to Twilio with every text, and Twilio reports there what became
                of it.
              </HookRow>
            )}
          </CardBody>
        </Card>
      )}

      {sections.numbers && <NumbersCard numbers={settings.numbers} tenantId={tenantId} sandbox={!twilio} />}

      {sections.check && <CheckCard check={check} unsaved={changed} />}
    </div>
  );
}

/** The check of the Twilio account: only for Twilio, and only once it is the saved provider. */
function CheckCard({ check, unsaved }: { check: ReturnType<typeof useSmsCheck>; unsaved: boolean }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Check"
        description="Reads your Twilio account and says what is still wrong. It changes nothing, here or at Twilio."
        action={
          <Button variant="outline" loading={check.isPending} disabled={unsaved} onClick={() => check.mutate()}>
            <Stethoscope />
            Check now
          </Button>
        }
      />
      <CardBody>
        {unsaved ? (
          <p className="text-xs text-text-muted">Save first. The check reads the settings as they are saved.</p>
        ) : check.error ? (
          <Callout tone="danger">
            {check.error instanceof ApiError ? check.error.message : "The check could not run. Please try again."}
          </Callout>
        ) : check.data ? (
          <CheckResult check={check.data} />
        ) : (
          <p className="text-xs text-text-muted">
            The keys, the Messaging Service and its Sender Pool, each number that is switched on, and the two
            triggers.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function HookRow({
  title, hook, lastWord, hasAddress, children,
}: {
  title: string;
  hook: SmsHook;
  lastWord: string;
  hasAddress: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-text">
          {title}
          {!hook.enabled && <Badge tone="warning">Off</Badge>}
        </p>
        <Link
          to={`/triggers/${hook.trigger_id}`}
          className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
        >
          {hook.name}
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      {hook.url ? (
        <p className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
          <span className="break-all font-mono text-xs text-text">{maskedUrl(hook.url)}</span>
          <CopyButton value={hook.url} label={`Copy the URL for ${title.toLowerCase()}`} />
        </p>
      ) : hook.problem ? (
        <Callout tone="warning">{hook.problem} Open the trigger to put it right.</Callout>
      ) : (
        <Callout tone="warning">
          {hasAddress ? "This URL cannot be built yet. Save the settings again." : "Set the public address to see this URL."}
        </Callout>
      )}
      <p className="text-xs text-text-muted">{children}</p>
      <p className="text-xs text-text-muted">
        {hook.last_received_at ? `${lastWord}: ${relativeTime(hook.last_received_at)}.` : "Nothing has arrived yet."}
      </p>
    </div>
  );
}

function NumbersCard({
  numbers, tenantId, sandbox,
}: {
  numbers: SmsNumber[];
  tenantId: number | undefined;
  /** In the sandbox no provider has registered anything: any number can be switched on. */
  sandbox: boolean;
}) {
  const toast = useToast();
  const setNumber = useSetSmsNumber(tenantId);
  const pending = setNumber.isPending ? setNumber.variables?.id : undefined;

  const toggle = async (number: SmsNumber, sms: boolean) => {
    try {
      await setNumber.mutateAsync({ id: number.id, sms });
    } catch (error) {
      toast.error(
        sms ? "Could not switch the number on" : "Could not switch the number off",
        error instanceof ApiError ? error.message : "Please try again.",
      );
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Numbers"
        description={
          sandbox
            ? "Which of your numbers send texts. A text leaves only from a number that is switched on, in the sandbox too."
            : "Which of your numbers send texts. Switch on the ones Twilio has registered to send. A number that is off still takes the texts sent to it."
        }
        action={
          numbers.length > 0 && (
            <Badge tone="neutral">
              {numbers.filter((number) => number.sms).length} of {numbers.length} send texts
            </Badge>
          )
        }
      />
      {numbers.length === 0 ? (
        <CardBody>
          <p className="text-xs text-text-muted">
            You hold no numbers yet.{" "}
            <Link to="/numbers" className="underline">
              Ask for one on Numbers.
            </Link>
          </p>
        </CardBody>
      ) : (
        // A tenant can hold dozens of numbers. The list scrolls inside the card, under its own
        // header, so the check below it stays in reach.
        <div className="[&>div]:max-h-[22.5rem] [&>div]:overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>A text to it lands in</TableHead>
                <TableHead className="w-28 text-right">Sends texts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {numbers.map((number) => (
                <TableRow key={number.id}>
                  <TableCell className="font-mono text-sm">{formatPhoneNumber(number.number)}</TableCell>
                  <TableCell className="text-sm text-text-muted">{inboxText(number)}</TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={number.sms}
                      disabled={pending === number.id}
                      onCheckedChange={(checked) => void toggle(number, checked)}
                      aria-label={`${formatPhoneNumber(number.number)} sends texts`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function CheckResult({ check }: { check: SmsCheck }) {
  return (
    <div className="space-y-3">
      <p className="flex flex-wrap items-center gap-2 text-sm text-text">
        <Badge tone={check.ok ? "success" : "warning"}>{checkSummary(check)}</Badge>
        <span className="text-xs text-text-muted">Checked {relativeTime(check.checked_at)}.</span>
      </p>
      <ul className="divide-y divide-border rounded-md border border-border">
        {check.items.map((item, index) => (
          <li key={`${item.code}-${item.number ?? index}`} className="flex items-start gap-2 px-3 py-2">
            {item.ok ? (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-9" aria-label="Right" />
            ) : (
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-9" aria-label="Needs attention" />
            )}
            {/* core-api's sentence says what it is about. Only one about a number needs the number. */}
            <p className="min-w-0 text-xs text-text">
              {item.number && <span className="font-mono font-medium">{formatPhoneNumber(item.number)} </span>}
              {item.detail}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
